import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Grid,
  IconButton,
  List,
  ListItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import type { Control, FieldArrayWithId, UseFieldArrayAppend, UseFieldArrayRemove } from "react-hook-form";
import { useFieldArray, useWatch } from "react-hook-form";
import ExpandButton from "../../components/entityCard/ExpandButton";
import DeleteIcon from "@mui/icons-material/Delete";
import FormTextField, {
  emailValidationPattern,
  phoneNumberValidationPattern,
} from "../../components/formComponents/FormTextField";
import Phone from "../../components/links/Phone";
import Email from "../../components/links/Email";
import type { ProviderFormState } from "./ProviderEdit";
import FormCheckbox from "../../components/formComponents/FormCheckbox";

interface PersonProps {
  readonly control: Control<ProviderFormState>;
  readonly index: number;
}

const PersonRow: FC<PersonProps> = ({ control, index }) => {
  const data = useWatch({
    control,
    name: `contacts.${index}`,
  });
  return (
    <TableRow>
      <TableCell>{data.name}</TableCell>
      <TableCell>{data.position}</TableCell>
      <TableCell>{data.phone && <Phone value={data.phone} />}</TableCell>
      <TableCell>{data.email && <Email value={data.email} />}</TableCell>
      <TableCell>
        <Checkbox checked={data.emailsRecipient} aria-label="Odbiorca e-maili" />
      </TableCell>
    </TableRow>
  );
};

interface CollapsedProps {
  readonly values: FieldArrayWithId<ProviderFormState, "contacts", "id">[];
  readonly control: Control<ProviderFormState>;
}

const CollapsedContactPersons: FC<CollapsedProps> = ({ values, control }) => {
  return (
    <Table>
      <TableBody>
        {values.map((v, idx) => (
          <PersonRow key={`${v.name}:${v.phone}:${v.email}`} index={idx} control={control} />
        ))}
      </TableBody>
    </Table>
  );
};

interface ExpandedPersonProps {
  readonly control: Control<ProviderFormState>;
  readonly index: number;
  readonly remove: UseFieldArrayRemove;
}

const ExpandedContactPerson: FC<ExpandedPersonProps> = ({ control, index, remove }) => {
  return (
    <ListItem
      secondaryAction={
        <IconButton edge="end" aria-label="Usuń osobę kontaktową" onClick={() => remove(index)}>
          <DeleteIcon />
        </IconButton>
      }
    >
      <Grid container size={12} spacing={1}>
        <Grid size={6}>
          <FormTextField
            control={control}
            name={`contacts.${index}.name`}
            label="Imię i nazwisko"
            rules={{ required: true }}
          />
        </Grid>
        <Grid size={3}>
          <FormTextField
            control={control}
            name={`contacts.${index}.phone`}
            label="Telefon"
            type="phone"
            rules={{
              pattern: phoneNumberValidationPattern,
            }}
          />
        </Grid>
        <Grid size={3}>
          <FormTextField
            control={control}
            name={`contacts.${index}.email`}
            label="Email"
            type="email"
            rules={{
              pattern: emailValidationPattern,
            }}
          />
        </Grid>
        <Grid size={3}>
          <FormTextField control={control} name={`contacts.${index}.position`} label="Stanowisko" />
        </Grid>
        <Grid size={3}>
          <FormTextField
            control={control}
            name={`contacts.${index}.loginEmail`}
            label="Email do logowania"
            type="email"
            rules={{
              required: true,
              pattern: emailValidationPattern,
            }}
          />
        </Grid>
        <Grid
          size={3}
          sx={{
            alignContent: "center",
          }}
        >
          <FormCheckbox name={`contacts.${index}.emailsRecipient`} control={control} label="Odbiorca e-maili" />
        </Grid>
      </Grid>
    </ListItem>
  );
};

interface ExpandedProps {
  readonly control: Control<ProviderFormState>;
  readonly values: FieldArrayWithId<ProviderFormState, "contacts", "id">[];
  readonly append: UseFieldArrayAppend<ProviderFormState, "contacts">;
  readonly remove: UseFieldArrayRemove;
}

const ExpandedContactPersons: FC<ExpandedProps> = ({ control, values, append, remove }) => {
  return (
    <>
      <List>
        {values.map((v, idx) => (
          <ExpandedContactPerson key={v.id} control={control} index={idx} remove={remove} />
        ))}
      </List>
      <Stack
        sx={{
          alignItems: "flex-end",
        }}
      >
        <Button
          onClick={() =>
            append({
              name: "",
              email: "",
              phone: "",
              loginEmail: "",
              position: "",
              emailsRecipient: true,
            })
          }
        >
          Dodaj
        </Button>
      </Stack>
    </>
  );
};

interface Props {
  readonly control: Control<ProviderFormState>;
  readonly disabled?: boolean;
}

const ContactPersons: FC<Props> = ({ control, disabled }) => {
  const [expanded, setExpanded] = useState(false);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "contacts",
  });
  return (
    <Card data-testid="contact-persons">
      <CardHeader subheader="Osoby kontaktowe" />
      <CardContent>
        {expanded && !disabled ? (
          <ExpandedContactPersons control={control} values={fields} append={append} remove={remove} />
        ) : (
          <CollapsedContactPersons control={control} values={fields} />
        )}
      </CardContent>
      {!disabled && (
        <CardActions>
          <ExpandButton expanded={expanded} onChange={setExpanded} />
        </CardActions>
      )}
    </Card>
  );
};

export default ContactPersons;
